import { Core } from "@faire/design-tokens";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web--source/common/media";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { optimizeImageUrl } from "@faire/web--source/common/transform-images";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import React from "react";
import styled, { css } from "styled-components";

const MAP_AUS_WIDTH = 115;
const MAP_AUS_HEIGHT = 118;
const MAP_BASE_WIDTH = 1026;
const MAP_BASE_HEIGHT = 587;
const MAP_EU_WIDTH = 142;
const MAP_EU_HEIGHT = 169;
const MAP_NA_WIDTH = 328;
const MAP_NA_HEIGHT = 290;

export const GeographicMapDesktop: React.FC = () => {
  const responsivelyRatio = useResponsiveValue({
    mobileAndAbove: 0,
    tabletAndAbove: MIN_WIDTH_BREAKPOINTS.TABLET / MIN_WIDTH_BREAKPOINTS.XLARGE,
    desktopAndAbove:
      MIN_WIDTH_BREAKPOINTS.DESKTOP / MIN_WIDTH_BREAKPOINTS.XLARGE,
    xLargeAndAbove: 1,
  });

  const imageWidth = Math.round(MAP_BASE_WIDTH * responsivelyRatio);
  const imageHeight = Math.round(MAP_BASE_HEIGHT * responsivelyRatio);

  const naWidth = Math.round(MAP_NA_WIDTH * responsivelyRatio);
  const naHeight = Math.round(MAP_NA_HEIGHT * responsivelyRatio);

  const euWidth = Math.round(MAP_EU_WIDTH * responsivelyRatio);
  const euHeight = Math.round(MAP_EU_HEIGHT * responsivelyRatio);

  const ausWidth = Math.round(MAP_AUS_WIDTH * responsivelyRatio);
  const ausHeight = Math.round(MAP_AUS_HEIGHT * responsivelyRatio);

  const isSSR = useIsSSR();

  return (
    <Wrapper justify="center">
      <Typography variant="displayLSerifRegular" align="center">
        <StrictLocalMsg
          defaultMessage="Get more sales at home and abroad"
          description={{ text: "Header for Faire geographic map section" }}
        />
      </Typography>
      <Spacer size="5x" />
      <MapWrapper>
        <BaseWrapper>
          <img
            src={optimizeImageUrl(
              {
                url: "https://cdn.faire.com/fastly/d892a95641090bd33c9c7de850b7b69cb0f2e93cb4286abbef40ee778da5e773.png",
              },
              isSSR,
              {
                width: imageWidth,
                height: imageHeight,
              }
            )}
            style={{
              width: `${imageWidth}px`,
              height: `${imageHeight}px`,
            }}
          />
        </BaseWrapper>
        <ContinentWrapper
          data-test-id="map-na"
          $top={Math.round(5 * responsivelyRatio)}
          $left={4}
          $width={naWidth}
          $height={naHeight}
        >
          <img
            src={optimizeImageUrl(
              {
                url: "https://cdn.faire.com/fastly/bd4035095bd157079050af6b8b652b86100d96d4ab5beaaf4e31da001feba74e.png",
              },
              isSSR,
              {
                width: naWidth,
                height: naHeight,
              }
            )}
            style={{
              width: `${naWidth}px`,
              height: `${naHeight}px`,
            }}
          />
        </ContinentWrapper>
        <ContinentWrapper
          data-test-id="map-eu"
          $top={Math.round(89 * responsivelyRatio)}
          $left={Math.round(453 * responsivelyRatio)}
          $width={euWidth}
          $height={euHeight}
        >
          <img
            src={optimizeImageUrl(
              {
                url: "https://cdn.faire.com/fastly/cb0233c90bc7b290426e078b697cd2629c7c9ab77926677c6d9a183591a1bf54.png",
              },
              isSSR,
              {
                width: euWidth,
                height: euHeight,
              }
            )}
            style={{
              width: `${euWidth}px`,
              height: `${euHeight}px`,
            }}
          />
        </ContinentWrapper>
        <ContinentWrapper
          data-test-id="map-aus"
          $top={Math.round(417 * responsivelyRatio)}
          $left={Math.round(799 * responsivelyRatio)}
          $width={ausWidth}
          $height={ausHeight}
        >
          <img
            src={optimizeImageUrl(
              {
                url: "https://cdn.faire.com/fastly/64050e66f041105ef61b351e29199f5ab39ff6a58443fffa46b6305485ab2a93.png",
              },
              isSSR,
              {
                width: ausWidth,
                height: ausHeight,
              }
            )}
            style={{
              width: `${ausWidth}px`,
              height: `${ausHeight}px`,
            }}
          />
        </ContinentWrapper>
      </MapWrapper>
      <TagWrapper justify="center">
        <ContinentInfoCardWrapper align="center" $isHighlighted>
          <Typography variant="subheadingSansMedium">
            <StrictLocalMsg
              defaultMessage="New markets"
              description={{
                text: "Faire is working on to support other countries and regions. New markets are coming soon",
              }}
            />
          </Typography>
          <Typography variant="displayMSerifRegular">
            <StrictLocalMsg
              defaultMessage="Coming soon"
              description={{
                text: "Faire is working on to support other countries and regions. New markets are coming soon",
              }}
            />
          </Typography>
        </ContinentInfoCardWrapper>
      </TagWrapper>
    </Wrapper>
  );
};

const Wrapper = styled(Column)`
  width: 100%;
  position: relative;
`;

const MapWrapper = styled.div`
  position: relative;
  margin: auto;
  width: ${MAP_BASE_WIDTH}px;
  height: 747px;

  ${responsiveMediaQueries.mobile} {
    width: 0px;
  }

  ${responsiveMediaQueries.tablet} {
    width: 550px;
    height: 480px;
  }

  ${responsiveMediaQueries.desktop} {
    width: 730px;
    height: 580px;
  }
`;

const BaseWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
`;

const ContinentWrapper = styled.div<{
  $left: number;
  $top: number;
  $width: number;
  $height: number;
}>`
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
  position: absolute;
  left: ${({ $left }) => css`
    ${$left}px
  `};
  top: ${({ $top }) => css`
    ${$top}px
  `};
  z-index: 2;
  filter: invert(50%) sepia(10%) saturate(666%) hue-rotate(6deg) brightness(96%)
    contrast(90%);
`;

const TagWrapper = styled(Row)`
  width: 100%;
  height: 150px;
  position: absolute;
  z-index: 3;
  bottom: 0;
`;

const ContinentInfoCardWrapper = styled(Column)<{
  $isHighlighted: boolean;
}>`
  padding: 24px;
  height: 150px;
  width: 438px;
  background-color: ${Core.surface.inverse};
  opacity: ${({ $isHighlighted: isHighlighted }) => (isHighlighted ? 1 : 0.5)};
  border-radius: 10px;
  box-sizing: border-box;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.25);
  border: 1px solid ${Core.border.muted};
`;
